import { getSdk } from "./sdkService";
import { config } from "../config/config";
import crypto from "crypto-js";
import { getLogger } from "../utils/logger";
import * as Sentry from "@sentry/react";
import { getBillingInfo } from "./billingService";

/**
 * Checks whether there is a valid token stored locally. In case the token exists, the app will move to logged in state.
 * In case of no token is present but user credentials are stored locally it tries to login to that account in
 * background.
 */

export async function updateJWTCookieFromLocalStorage() {
  console.log("AutoLogin: Trying to Update cookie from Storage");
  const sdk = await getSdk();
  if (localStorage) {
    const token = localStorage.getItem("userJWT");
    token && sdk.userManager.updateUserCookie(token);
    token && sdk.userManager.updateUserToken(token);
  }
}
export async function autologin() {
  const sdk = await getSdk();
  const loggedIn = await sdk.authenticationManager.isLoggedIn();
  const logger = getLogger();
  logger.log({ loggedIn }, "autologin");
  Sentry.captureMessage("Attempting autologin");
  if (loggedIn) {
    const user = await sdk.userManager.get();
    Sentry.setUser(user);
    await sdk.userManager.get({ avoidCache: true });
    getBillingInfo();
    return user;
  } else if (localStorage) {
    console.log(`=======AUTOLOGIN-3=========`);
    const encryptedCredentials = localStorage.getItem("credentials");
    if (encryptedCredentials) {
      console.log(`=======AUTOLOGIN-4=========`);
      let credentials = "";
      try {
        credentials = decrypt(encryptedCredentials);
      } catch {
        Sentry.captureMessage("autologin-LoggedOut: Stored credentials didn't decrypt");
        credentials = "{}";
      }
      const { userName, password } = JSON.parse(credentials);
      if (userName && password) {
        console.log(`=======AUTOLOGIN-6=========`);
        try {
          Sentry.captureMessage("autologin: Trying to login using stored credentials for user.");
          logger.log(`Trying to login using stored credentials for user ${userName}`, "autologin");
          const loginResponse = await login(userName, password);
          return !loginResponse.config && loginResponse;
        } catch (e) {
          Sentry.captureMessage("autologin-LoggedOut: Autologin using stored credentials failed for user.");
          logger.log(`Autologin using stored credentials failed for user ${userName}. Logging out`, "autologin");
          await logout();
        }
      }
    } else {
      console.log(`=======AUTOLOGIN-6=========`);
      try {
        await updateJWTCookieFromLocalStorage();
        const loggedIn = await sdk.authenticationManager.isLoggedIn();
        if (loggedIn) {
          console.log(`=======AUTOLOGIN-7=========`);
          let isLoggedInUser = await sdk.userManager.get({ avoidCache: true });
          return isLoggedInUser;
        }
      } catch (error) {
        logger.log(error);
      }
      const configuration = config.get();
      const { autologin } = configuration.debug;
      if (autologin && autologin.userName && autologin.password) {
        console.log(`=======AUTOLOGIN-8=========`);
        logger.log(`Trying to login using config file credentials for user ${autologin.userName}`, "autologin");
        const loginResponse = await login(autologin.userName, autologin.password);
        return !loginResponse.config && loginResponse;
      }
    }
  }
  return false;
}

/**
 * Uses the SDK to perform a login and in case of being successful, the app store is notified that the user is logged in
 * so that the app UI is updated accordingly.
 * @param {string} username The user name of the account found in local storage
 * @param {string} password The password of the account found in local storage
 * @returns {Promise}
 */
export async function login(username, password, offline = false) {
  try {
    const sdk = await getSdk();
    const loginResponse = await sdk.authenticationManager.login(
      {
        userName: username,
        password: password
      },
      offline
    );
    storeCredentials(username, password);
    // Parallel Call billing api if user logged In and set in Context api for further use
    getBillingInfo(true);
    return loginResponse;
  } catch (error) {
    throw error;
  }
}

/**
 * Logs the current user out and updates the app store accordingly so that the UI can be changed to show the new state
 */
export async function logout(isResetXToken = false) {
  const sdk = await getSdk();
  await sdk.authenticationManager.logout(isResetXToken);
  removeLocalUserData();
}

/**
 * Uses the SDK to perform a signup request with some data provided by the user.
 * @param {String} user - User's account future identifier. For now it will be an e-mail address, but it can be a mobile
 * phone number as well.
 * @param {String} password - User's account password to be able to access to the app when logging in.
 * @returns {Promise}
 */
export async function signup({ user, password }) {
  try {
    const sdk = await getSdk();
    return await sdk.authenticationManager.signup({
      user,
      // NOTE: Password might be optional in future signup implementations and in this case if password param is not received
      // it won't be created in the object sent to the SDK. for now since Signup page component is disabling signup request
      //button until its input value is filled, this 'password' field will be always sent.
      ...(password && { password: password })
    });
  } catch (error) {
    throw error;
  }
}

export async function getUserId() {
  const sdk = await getSdk();
  return await sdk.authenticationManager.getUserId();
}

/**
 * If localStorage is present in the system, the received credentials are encrypted and then stored locally.
 * @param {string} username User name of the account that needs to be stored locally
 * @param {string} password Password of the account that needs to be stored locally
 * @param {string} password Password of the account that needs to be stored locally
 */
export function storeCredentials(username, password) {
  if (localStorage) {
    const credentials = {
      userName: username,
      password: password
    };
    const encryptedCredentials = encrypt(credentials);
    localStorage.setItem("credentials", encryptedCredentials);
  }
}

function removeLocalUserData() {
  if (localStorage) {
    localStorage.removeItem("credentials");
    localStorage.removeItem("userJWT");
    localStorage.removeItem("showVoucherPopup");
  }
}

function encrypt(object) {
  return crypto.AES.encrypt(JSON.stringify(object), config.get().authenticationKey).toString();
}

function decrypt(object) {
  return crypto.AES.decrypt(object, config.get().authenticationKey).toString(crypto.enc.Utf8);
}
